import type {
  ArticleCategory,
  Unit,
  Article,
  RecipeCategory,
  RecipeIngredient,
  RecipeStep,
  RecipeMember,
  Recipe,
  ShoppingCartItem,
  RecurringPurchase,
  DeletedItem
} from '../types/domain'

export type {
  ArticleCategory,
  Unit,
  Article,
  RecipeCategory,
  RecipeIngredient,
  RecipeStep,
  RecipeMember,
  Recipe,
  ShoppingCartItem,
  RecurringPurchase,
  DeletedItem
}

export const initialUnits: Unit[] = [
  { name: 'g',        forShopping: true,  forRecipes: true  },
  { name: 'kg',       forShopping: true,  forRecipes: true  },
  { name: 'ml',       forShopping: true,  forRecipes: true  },
  { name: 'l',        forShopping: true,  forRecipes: true  },
  { name: 'Stück',    forShopping: true,  forRecipes: true  },
  { name: 'Packung',  forShopping: true,  forRecipes: true  },
  { name: 'EL',       forShopping: false, forRecipes: true  },
  { name: 'TL',       forShopping: false, forRecipes: true  },
  { name: 'Prise',    forShopping: false, forRecipes: true  },
  { name: 'Bund',     forShopping: true,  forRecipes: true  },
  { name: 'Dose',     forShopping: true,  forRecipes: true  },
  { name: 'Flasche',  forShopping: true,  forRecipes: true  },
]

export const initialArticleCategories: ArticleCategory[] = [
  { id: 'cat-bakery', name: 'Bäckerei', img: 'bakery.png', color: '240,200,150' },
  { id: 'cat-dairy', name: 'Molkereiprodukte', img: 'dairy.jpg', color: '240,230,220' },
  { id: 'cat-drinks', name: 'Getränke', img: 'drinks.jpg', color: '250,210,120' },
  { id: 'cat-frozen', name: 'Tiefkühlwaren', img: 'frozen.jpg', color: '130,200,240' },
  { id: 'cat-fruit', name: 'Obst', img: 'fruit.jpg', color: '240,150,150' },
  { id: 'cat-groceries', name: 'Lebensmittel', img: 'groceries.jpg', color: '220,210,190' },
  { id: 'cat-hygiene', name: 'Drogerie & Hygiene', img: 'hygiene.jpg', color: '220,220,240' },
  { id: 'cat-meat', name: 'Fleisch & Wurst', img: 'meat.jpg', color: '230,170,160' },
  { id: 'cat-misc', name: 'Sonstiges', img: 'miscellaneous.jpg', color: '200,200,200' },
  { id: 'cat-spices', name: 'Gewürze', img: 'spices.jpg', color: '190,140,110' },
  { id: 'cat-sweets', name: 'Süßigkeiten', img: 'sweets.jpg', color: '240,160,200' },
  { id: 'cat-vegetables', name: 'Gemüse', img: 'vegetables.jpg', color: '150,200,120' }
]

export const initialArticles: Article[] = [
  { id: 'art-ananas', name: 'Ananas', img: 'ananas.jpg', categoryID: 'cat-fruit', unit: 'Stück' },
  { id: 'art-apfel', name: 'Apfel', img: 'apfel.jpg', categoryID: 'cat-fruit', unit: 'Stück' },
  { id: 'art-apfelsaft', name: 'Apfelsaft', img: 'apfelsaft.jpg', categoryID: 'cat-drinks', unit: 'Liter' },
  { id: 'art-baguette', name: 'Baguette', img: 'baguette.jpg', categoryID: 'cat-bakery', unit: 'Stück' },
  { id: 'art-banane', name: 'Banane', img: 'banane.png', categoryID: 'cat-fruit', unit: 'Stück' },
  { id: 'art-bier', name: 'Bier', img: 'drinks.jpg', categoryID: 'cat-drinks', unit: 'Flasche' },
  { id: 'art-broetchen', name: 'Brötchen', img: 'broetchen.jpg', categoryID: 'cat-bakery', unit: 'Stück' },
  { id: 'art-brot', name: 'Brot', img: 'brot.jpg', categoryID: 'cat-bakery', unit: 'Stück' },
  { id: 'art-bruehe', name: 'Brühe', img: 'bruehe.jpg', categoryID: 'cat-spices', unit: 'Glas' },
  { id: 'art-butter', name: 'Butter', img: 'dairy.jpg', categoryID: 'cat-dairy', unit: 'Packung' },
  { id: 'art-chips', name: 'Chips', img: 'chips.jpg', categoryID: 'cat-sweets', unit: 'Tüte' },
  { id: 'art-cola', name: 'Cola', img: 'drinks.jpg', categoryID: 'cat-drinks', unit: 'Flasche' },
  { id: 'art-cremefraiche', name: 'Crème Fraîche', img: 'cremeFraiche.jpg', categoryID: 'cat-dairy', unit: 'Becher' },
  { id: 'art-curry', name: 'Currypulver', img: 'curry.jpg', categoryID: 'cat-spices', unit: 'Dose' },
  { id: 'art-eier', name: 'Eier', img: 'dairy.jpg', categoryID: 'cat-dairy', unit: 'Packung' },
  { id: 'art-eis', name: 'Eiscreme', img: 'eis.jpg', categoryID: 'cat-frozen', unit: 'Packung' },
  { id: 'art-erbsen', name: 'Erbsen (TK)', img: 'erbsen.jpg', categoryID: 'cat-frozen', unit: 'Packung' },
  { id: 'art-erdbeeren', name: 'Erdbeeren', img: 'erdbeeren.jpg', categoryID: 'cat-fruit', unit: 'g' },
  { id: 'art-gurke', name: 'Gurke', img: 'vegetables.jpg', categoryID: 'cat-vegetables', unit: 'Stück' },
  { id: 'art-haehnchen', name: 'Hähnchenbrust', img: 'meat.jpg', categoryID: 'cat-meat', unit: 'g' },
  { id: 'art-hackfleisch', name: 'Hackfleisch', img: 'hackfleisch.jpg', categoryID: 'cat-meat', unit: 'g' },
  { id: 'art-kaffee', name: 'Kaffee', img: 'drinks.jpg', categoryID: 'cat-drinks', unit: 'Packung' },
  { id: 'art-kaese', name: 'Käse (Scheiben)', img: 'kaese.jpg', categoryID: 'cat-dairy', unit: 'Packung' },
  { id: 'art-kartoffeln', name: 'Kartoffeln', img: 'kartoffeln.png', categoryID: 'cat-vegetables', unit: 'kg' },
  { id: 'art-ketchup', name: 'Ketchup', img: 'ketchup.jpg', categoryID: 'cat-groceries', unit: 'Flasche' },
  { id: 'art-knoblauch', name: 'Knoblauch', img: 'vegetables.jpg', categoryID: 'cat-vegetables', unit: 'Knolle' },
  { id: 'art-kochschinken', name: 'Kochschinken', img: 'kochschinken.jpg', categoryID: 'cat-meat', unit: 'Packung' },
  { id: 'art-lauch', name: 'Lauch', img: 'lauch.jpg', categoryID: 'cat-vegetables', unit: 'Stange' },
  { id: 'art-mais', name: 'Mais', img: 'mais.jpg', categoryID: 'cat-groceries', unit: 'Dose' },
  { id: 'art-mehl', name: 'Mehl', img: 'groceries.jpg', categoryID: 'cat-groceries', unit: 'kg' },
  { id: 'art-milch', name: 'Milch', img: 'milch.jpg', categoryID: 'cat-dairy', unit: 'Liter' },
  { id: 'art-moehren', name: 'Möhren', img: 'moehren.png', categoryID: 'cat-vegetables', unit: 'g' },
  { id: 'art-olivenoel', name: 'Olivenöl', img: 'spices.jpg', categoryID: 'cat-spices', unit: 'Flasche' },
  { id: 'art-orange', name: 'Orange', img: 'orange.jpg', categoryID: 'cat-fruit', unit: 'Stück' },
  { id: 'art-orangensaft', name: 'Orangensaft', img: 'orangensaft.jpg', categoryID: 'cat-drinks', unit: 'Liter' },
  { id: 'art-paprika', name: 'Paprika', img: 'paprika.jpg', categoryID: 'cat-vegetables', unit: 'Stück' },
  { id: 'art-penne', name: 'Penne Nudeln', img: 'penne.jpg', categoryID: 'cat-groceries', unit: 'g' },
  { id: 'art-pfeffer', name: 'Pfeffer', img: 'pfeffer.jpg', categoryID: 'cat-spices', unit: 'Prise' },
  { id: 'art-pilze', name: 'Pilze', img: 'pilze.jpeg', categoryID: 'cat-vegetables', unit: 'g' },
  { id: 'art-pizza', name: 'Pizza (TK)', img: 'pizza.jpg', categoryID: 'cat-frozen', unit: 'Stück' },
  { id: 'art-pommes', name: 'Pommes (TK)', img: 'pommes.png', categoryID: 'cat-frozen', unit: 'Tüte' },
  { id: 'art-quark', name: 'Quark', img: 'quark.jpg', categoryID: 'cat-dairy', unit: 'Becher' },
  { id: 'art-reibekaese', name: 'Reibekäse', img: 'reibekaese.jpg', categoryID: 'cat-dairy', unit: 'g' },
  { id: 'art-reis', name: 'Reis', img: 'reis.jpg', categoryID: 'cat-groceries', unit: 'g' },
  { id: 'art-sahne', name: 'Sahne', img: 'sahne.jpg', categoryID: 'cat-dairy', unit: 'Becher' },
  { id: 'art-salami', name: 'Salami', img: 'salami.jpg', categoryID: 'cat-meat', unit: 'Packung' },
  { id: 'art-salat', name: 'Salat', img: 'salat.jpg', categoryID: 'cat-vegetables', unit: 'Kopf' },
  { id: 'art-salz', name: 'Salz', img: 'salz.jpg', categoryID: 'cat-spices', unit: 'Prise' },
  { id: 'art-schokolade', name: 'Schokolade', img: 'schokolade.jpg', categoryID: 'cat-sweets', unit: 'Tafel' },
  { id: 'art-senf', name: 'Senf', img: 'groceries.jpg', categoryID: 'cat-groceries', unit: 'Glas' },
  { id: 'art-spaghetti', name: 'Spaghetti', img: 'spaghetti.jpg', categoryID: 'cat-groceries', unit: 'g' },
  { id: 'art-spinat', name: 'Spinat (TK)', img: 'spinat.jpg', categoryID: 'cat-frozen', unit: 'Packung' },
  { id: 'art-tee', name: 'Tee', img: 'drinks.jpg', categoryID: 'cat-drinks', unit: 'Packung' },
  { id: 'art-toast', name: 'Toastbrot', img: 'toast.jpg', categoryID: 'cat-bakery', unit: 'Packung' },
  { id: 'art-tomaten', name: 'Tomaten', img: 'tomaten.jpg', categoryID: 'cat-vegetables', unit: 'g' },
  { id: 'art-wasser', name: 'Wasser (Sprudel)', img: 'wasser.jpg', categoryID: 'cat-drinks', unit: 'Flasche' },
  { id: 'art-weintrauben', name: 'Weintrauben', img: 'weintrauben.jpg', categoryID: 'cat-fruit', unit: 'g' },
  { id: 'art-zucker', name: 'Zucker', img: 'groceries.jpg', categoryID: 'cat-groceries', unit: 'kg' },
  { id: 'art-zucchini', name: 'Zucchini', img: 'vegetables.jpg', categoryID: 'cat-vegetables', unit: 'Stück' },
  { id: 'art-zwiebeln', name: 'Zwiebeln', img: 'zwiebeln.jpg', categoryID: 'cat-vegetables', unit: 'Stück' }
]

export const initialRecipeCategories: RecipeCategory[] = [
  { id: 'rcat-main', name: 'Hauptspeise' },
  { id: 'rcat-dessert', name: 'Nachspeise' },
  { id: 'rcat-starter', name: 'Vorspeise' },
  { id: 'rcat-snack', name: 'Snack' }
]

export const initialRecipes: Recipe[] = [
  {
    id: 'rec-curry-chicken',
    name: 'Curryhühnchen',
    img: 'curryChicken.jpg',
    dominantColor: '215, 145, 30',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-curry', amount: '2 TL' },
      { articleID: 'art-sahne', amount: '200 ml' },
      { articleID: 'art-reis', amount: '200 g' }
    ],
    steps: [
      'Den Reis in leicht gesalzenem Wasser kochen.',
      'Währenddessen das Hähnchenfleisch in Würfel schneiden und mit Curry anbraten.',
      'Mit Sahne ablöschen und aufkochen lassen.',
      'Alles zusammen anrichten und servieren.'
    ],
    favorite: true,
    tags: ['Pfanne', 'Einfach']
  },
  {
    id: 'rec-spaghetti-bolognese',
    name: 'Spaghetti Bolognese',
    img: 'spaghettiBolognese.jpg',
    dominantColor: '183, 65, 14',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-spaghetti', amount: '500 g' },
      { articleID: 'art-hackfleisch', amount: '500 g' },
      { articleID: 'art-tomaten', amount: '400 g' },
      { articleID: 'art-zwiebeln', amount: '2 Stück' }
    ],
    steps: [
      'Zwiebeln hacken und mit dem Hackfleisch scharf anbraten.',
      'Die Tomaten hinzufügen und die Soße bei kleiner Hitze köcheln lassen.',
      'Parallel die Spaghetti in Salzwasser al dente kochen.',
      'Nudeln mit der Soße anrichten und optional mit Reibekäse servieren.'
    ],
    favorite: false,
    tags: ['Klassiker', 'Einfach']
  },
  {
    id: 'rec-lasagne',
    name: 'Lasagne',
    img: 'lasagne.jpg',
    dominantColor: '205, 90, 35',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-hackfleisch', amount: '500 g' },
      { articleID: 'art-tomaten', amount: '400 g' },
      { articleID: 'art-reibekaese', amount: '200 g' }
    ],
    steps: [
      'Eine Hackfleischsoße zubereiten (siehe Bolognese).',
      'Abwechselnd Nudelplatten und Soße in eine Auflaufform schichten.',
      'Mit reichlich Reibekäse bestreuen.',
      'Im Ofen bei 200°C für ca. 30 Minuten backen, bis der Käse goldbraun ist.'
    ],
    favorite: true,
    tags: ['Auflauf', 'Klassiker']
  },
  {
    id: 'rec-kartoffelsuppe',
    name: 'Kartoffelsuppe',
    img: '',
    dominantColor: '220, 200, 130',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-kartoffeln', amount: '800 g' },
      { articleID: 'art-zwiebeln', amount: '2 Stück' },
      { articleID: 'art-lauch', amount: '1 Stange' },
      { articleID: 'art-sahne', amount: '200 ml' },
      { articleID: 'art-bruehe', amount: '1 l' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' }
    ],
    steps: [
      'Kartoffeln schälen und in Würfel schneiden. Zwiebeln und Lauch klein schneiden.',
      'Zwiebeln und Lauch in etwas Butter andünsten.',
      'Kartoffelwürfel dazugeben und mit Brühe aufgießen.',
      'Ca. 20 Minuten köcheln lassen, bis die Kartoffeln weich sind.',
      'Einen Teil stampfen (nicht alles – Stücke lassen!), mit Sahne verfeinern.',
      'Mit Salz und Pfeffer abschmecken. Optional mit Kochschinken-Würfeln servieren.'
    ],
    favorite: false,
    tags: ['Suppe', 'Herbst', 'Einfach']
  },
  {
    id: 'rec-pfannkuchen',
    name: 'Pfannkuchen',
    img: '',
    dominantColor: '230, 200, 100',
    categoryID: 'rcat-dessert',
    ingredients: [
      { articleID: 'art-mehl', amount: '250 g' },
      { articleID: 'art-milch', amount: '400 ml' },
      { articleID: 'art-eier', amount: '3 Stück' },
      { articleID: 'art-zucker', amount: '2 EL' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-butter', amount: '20 g' }
    ],
    steps: [
      'Mehl, Milch, Eier, Zucker und eine Prise Salz zu einem glatten Teig verrühren.',
      'Den Teig 15 Minuten ruhen lassen.',
      'Eine Pfanne mit etwas Butter erhitzen.',
      'Dünne Pfannkuchen von beiden Seiten goldbraun ausbacken.',
      'Mit Zucker, Nutella, Marmelade oder frischen Früchten servieren.'
    ],
    favorite: true,
    tags: ['Dessert', 'Für Kinder', 'Schnell']
  },
  {
    id: 'rec-tomatensuppe',
    name: 'Tomatensuppe',
    img: '',
    dominantColor: '200, 60, 30',
    categoryID: 'rcat-starter',
    ingredients: [
      { articleID: 'art-tomaten', amount: '800 g' },
      { articleID: 'art-zwiebeln', amount: '1 Stück' },
      { articleID: 'art-knoblauch', amount: '2 Zehen' },
      { articleID: 'art-olivenoel', amount: '2 EL' },
      { articleID: 'art-sahne', amount: '100 ml' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' }
    ],
    steps: [
      'Zwiebeln und Knoblauch fein hacken und in Olivenöl andünsten.',
      'Tomaten (Dose oder frisch) hinzugeben und 15 Minuten köcheln lassen.',
      'Mit einem Stabmixer pürieren.',
      'Sahne einrühren und mit Salz und Pfeffer abschmecken.',
      'Mit frischem Baguette servieren.'
    ],
    favorite: false,
    tags: ['Suppe', 'Vegetarisch', 'Schnell']
  },
  {
    id: 'rec-kartoffelpuffer',
    name: 'Kartoffelpuffer',
    img: '',
    dominantColor: '200, 180, 100',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-kartoffeln', amount: '1 kg' },
      { articleID: 'art-zwiebeln', amount: '1 Stück' },
      { articleID: 'art-eier', amount: '2 Stück' },
      { articleID: 'art-mehl', amount: '3 EL' },
      { articleID: 'art-salz', amount: '1 TL' }
    ],
    steps: [
      'Kartoffeln schälen und fein reiben. Zwiebel ebenfalls reiben.',
      'Überschüssiges Wasser gut ausdrücken.',
      'Eier, Mehl und Salz untermischen.',
      'In einer Pfanne mit reichlich Öl kleine Puffer von beiden Seiten knusprig braten.',
      'Auf Küchenpapier abtropfen lassen. Mit Apfelmus oder Lachs servieren.'
    ],
    favorite: false,
    tags: ['Pfanne', 'Klassiker', 'Für Kinder']
  },
  {
    id: 'rec-gemuese-pfanne',
    name: 'Bunte Gemüsepfanne',
    img: '',
    dominantColor: '120, 180, 80',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-paprika', amount: '2 Stück' },
      { articleID: 'art-zucchini', amount: '1 Stück' },
      { articleID: 'art-pilze', amount: '200 g' },
      { articleID: 'art-zwiebeln', amount: '1 Stück' },
      { articleID: 'art-knoblauch', amount: '2 Zehen' },
      { articleID: 'art-olivenoel', amount: '3 EL' },
      { articleID: 'art-reis', amount: '200 g' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' }
    ],
    steps: [
      'Reis nach Packungsanleitung kochen.',
      'Gemüse waschen und in mundgerechte Stücke schneiden.',
      'Olivenöl in einer großen Pfanne erhitzen.',
      'Zwiebeln und Knoblauch darin glasig dünsten.',
      'Restliches Gemüse dazugeben und bei starker Hitze ca. 8 Minuten braten.',
      'Mit Salz und Pfeffer würzen, zusammen mit dem Reis servieren.'
    ],
    favorite: false,
    tags: ['Vegetarisch', 'Schnell', 'Pfanne']
  },
  {
    id: 'rec-schnitzel',
    name: 'Schnitzel mit Pommes',
    img: '',
    dominantColor: '200, 160, 80',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-haehnchen', amount: '400 g' },
      { articleID: 'art-eier', amount: '2 Stück' },
      { articleID: 'art-mehl', amount: '100 g' },
      { articleID: 'art-pommes', amount: '1 Tüte' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' },
      { articleID: 'art-ketchup', amount: '' }
    ],
    steps: [
      'Hähnchenbrust flach klopfen, mit Salz und Pfeffer würzen.',
      'Nacheinander in Mehl, verquirltem Ei und Semmelbröseln wenden.',
      'In reichlich Öl oder Butterschmalz von beiden Seiten goldbraun braten.',
      'Pommes im Ofen nach Packungsanleitung zubereiten.',
      'Zusammen mit Ketchup servieren.'
    ],
    favorite: true,
    tags: ['Klassiker', 'Für Kinder']
  },
  {
    id: 'rec-nudelsalat',
    name: 'Nudelsalat',
    img: '',
    dominantColor: '190, 170, 100',
    categoryID: 'rcat-starter',
    ingredients: [
      { articleID: 'art-penne', amount: '500 g' },
      { articleID: 'art-paprika', amount: '1 Stück' },
      { articleID: 'art-gurke', amount: '1 Stück' },
      { articleID: 'art-mais', amount: '1 Dose' },
      { articleID: 'art-kochschinken', amount: '100 g' },
      { articleID: 'art-olivenoel', amount: '4 EL' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' }
    ],
    steps: [
      'Nudeln al dente kochen, abgießen und abkühlen lassen.',
      'Paprika, Gurke und Kochschinken in kleine Würfel schneiden.',
      'Mais abtropfen lassen.',
      'Alles in einer großen Schüssel vermengen.',
      'Mit Olivenöl, Salz und Pfeffer anmachen.',
      'Mindestens 1 Stunde im Kühlschrank durchziehen lassen.'
    ],
    favorite: false,
    tags: ['Party', 'Einfach', 'Meal Prep']
  },
  {
    id: 'rec-omelette',
    name: 'Omelette mit Gemüse',
    img: '',
    dominantColor: '220, 190, 80',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-eier', amount: '4 Stück' },
      { articleID: 'art-paprika', amount: '1 Stück' },
      { articleID: 'art-pilze', amount: '100 g' },
      { articleID: 'art-reibekaese', amount: '50 g' },
      { articleID: 'art-butter', amount: '1 EL' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' }
    ],
    steps: [
      'Eier verquirlen und mit Salz und Pfeffer würzen.',
      'Paprika und Pilze in kleine Stücke schneiden.',
      'Butter in einer Pfanne erhitzen, Gemüse kurz anbraten.',
      'Eiermasse darüber gießen und bei mittlerer Hitze stocken lassen.',
      'Reibekäse darüber streuen, zusammenklappen und servieren.'
    ],
    favorite: false,
    tags: ['Frühstück', 'Schnell', 'Vegetarisch', 'Low Carb']
  },
  {
    id: 'rec-reis-hackfleisch-pfanne',
    name: 'Reis-Hackfleisch-Pfanne',
    img: '',
    dominantColor: '180, 120, 60',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-reis', amount: '250 g' },
      { articleID: 'art-hackfleisch', amount: '400 g' },
      { articleID: 'art-paprika', amount: '1 Stück' },
      { articleID: 'art-zwiebeln', amount: '1 Stück' },
      { articleID: 'art-tomaten', amount: '400 g' },
      { articleID: 'art-salz', amount: '1 Prise' },
      { articleID: 'art-pfeffer', amount: '1 Prise' }
    ],
    steps: [
      'Reis nach Packungsanleitung kochen.',
      'Zwiebeln würfeln und in etwas Öl glasig dünsten.',
      'Hackfleisch dazugeben und krümelig braten.',
      'Paprika in Streifen schneiden und mitbraten.',
      'Tomaten hinzufügen und 10 Minuten köcheln lassen.',
      'Mit Salz und Pfeffer abschmecken, mit dem Reis servieren.'
    ],
    favorite: false,
    tags: ['Pfanne', 'Einfach', 'Meal Prep']
  },
  {
    id: 'rec-kaesespaetzle',
    name: 'Käsespätzle',
    img: '',
    dominantColor: '220, 180, 60',
    categoryID: 'rcat-main',
    ingredients: [
      { articleID: 'art-mehl', amount: '400 g' },
      { articleID: 'art-eier', amount: '4 Stück' },
      { articleID: 'art-reibekaese', amount: '300 g' },
      { articleID: 'art-zwiebeln', amount: '3 Stück' },
      { articleID: 'art-butter', amount: '50 g' },
      { articleID: 'art-salz', amount: '1 TL' }
    ],
    steps: [
      'Mehl, Eier, Salz und ca. 150 ml Wasser zu einem zähen Teig verrühren. 15 Minuten ruhen lassen.',
      'Spätzle portionsweise in kochendes Salzwasser schaben oder durch eine Spätzlepresse drücken.',
      'Wenn die Spätzle oben schwimmen, abschöpfen.',
      'Zwiebeln in Ringe schneiden und in Butter goldbraun rösten.',
      'Spätzle und Reibekäse abwechselnd in eine Form schichten.',
      'Kurz in den Ofen (200°C) bis der Käse geschmolzen ist. Mit Röstzwiebeln servieren.'
    ],
    favorite: true,
    tags: ['Vegetarisch', 'Klassiker', 'Auflauf']
  }
]

export const DEFAULT_RECIPE_TAGS: string[] = [
  'Vegetarisch',
  'Vegan',
  'Laktosefrei',
  'Glutenfrei',
  'Low Carb',
  'High Protein',
  'Zuckerfrei',
  'Schnell',
  'Einfach',
  'Meal Prep',
  'One Pot',
  'Für Kinder',
  'Party',
  'Grillen',
  'Backen',
  'Dessert',
  'Frühstück',
  'Suppe',
  'Salat',
  'Sommer',
  'Winter'
]

export const initialShoppingList: ShoppingCartItem[] = [
  { id: 'demo-item-1', articleID: 'art-apfel', articleName: 'Apfel', amount: '4 Stück', checked: false, note: 'Sorte Elstar' },
  { id: 'demo-item-2', articleID: 'art-milch', articleName: 'Milch', amount: '2 Liter', checked: false },
  { id: 'demo-item-3', articleID: 'art-brot', articleName: 'Brot', amount: '1 Laib', checked: false },
  { id: 'demo-item-4', articleID: 'art-spaghetti', articleName: 'Spaghetti', amount: '500 g', checked: true },
  { id: 'demo-item-5', articleID: 'art-tomaten', articleName: 'Tomaten', amount: '400 g', checked: true }
]
